import React from 'react';

import { Alert, Flex, Paper, PaperProps, ScrollArea, useMantineTheme } from '@mantine/core';
import { IconAlertCircle, IconShoppingCart } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { AddStoreButton } from 'components/AddStoreButton';
import { FullScreenLoader } from 'components/FullScreenLoader';
import { NoDataFound } from 'components/NoDataFound';
import { ShopifyStoreTile } from 'pages/Settings/SettingTabs/StoresSettingsTab/ShopifyStoreTile';
import { useTranslation } from 'react-i18next';
import { ShopifyService } from 'services/shopify-service';
import { SHOPIFY_STORES_QUERY_KEY } from 'shared/constants/query-keys';
import { PRIMARY_SHADE_WHITE } from 'shared/constants/theme';
import { ShopifyStore } from 'shared/interfaces/ShopifyStore';

interface StoreSectionProps extends PaperProps {
  selectedResourceStoreId: string;
  onStoreClick: (resourceStoreId: string) => void;
  showError?: boolean;
}

export function StoreSection({ selectedResourceStoreId, onStoreClick, showError, ...paperProps }: StoreSectionProps) {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const shopifyStoresQuery = useQuery({
    queryFn: ShopifyService.getAllStores,
    queryKey: [SHOPIFY_STORES_QUERY_KEY]
  });

  if (shopifyStoresQuery.isInitialLoading) return <FullScreenLoader w="100%" h={paperProps.h ?? '100%'} />;
  const shopifyStores: ShopifyStore[] = shopifyStoresQuery.data || [];

  return (
    <>
      <Paper
        {...paperProps}
        style={{
          borderColor: showError ? theme.colors.red[PRIMARY_SHADE_WHITE] : undefined
        }}>
        <ScrollArea h={shopifyStores.length === 0 ? 0 : '100%'}>
          {shopifyStores.map((store, idx) => (
            <ShopifyStoreTile
              key={idx}
              mt={idx === 0 ? 0 : 5}
              mb={idx === shopifyStores.length - 1 ? 0 : 5}
              shopifyStore={store}
              selectedResourceStoreId={selectedResourceStoreId}
              onSelect={(resourceId: string) => onStoreClick(resourceId)}
            />
          ))}
        </ScrollArea>
        {shopifyStores.length === 0 && (
          <NoDataFound
            h="100%"
            size="xs"
            title={t('NO_DATA_FOUND.STORES.TITLE')}
            subTitle={t('NO_DATA_FOUND.STORES.SUB_TITLE')}
            icon={<IconShoppingCart />}
            button={<AddStoreButton size="xs" />}
          />
        )}
      </Paper>
      <Flex direction="column" align="center" justify="center" mx="auto" mt={10}>
        {showError && (
          <Alert
            my={5}
            icon={<IconAlertCircle />}
            title={t('SMART_ASSISTANT.STORE_SECTION.NO_STORE_SELECTED.TITLE')}
            color="red"
            maw={300}>
            {t('SMART_ASSISTANT.STORE_SECTION.NO_STORE_SELECTED.DESCRIPTION')}
          </Alert>
        )}
      </Flex>
    </>
  );
}
